/*!
 * ext.js — Extension cue handler for OscillaScore
 * -----------------------------------------------------------
 * Calls functions from score-specific extension modules.
 *
 * DSL Syntax:
 *   ext(fn:moduleName.functionName)
 *   ext(fn:moduleName.functionName, on:select)
 *   ext(fn:moduleName.functionName, on:click)
 *   ext(fn:moduleName.functionName, on:load)
 *
 * The extension function receives a context object:
 *   {
 *     event: "select" | "click" | "load" | etc,
 *     selected: "triangleA",  // for select events
 *     element: <SVGElement>,  // the cue element
 *     all: [...],             // all selectable elements (for select)
 *     paramBus: oscillaParamBus,
 *     ... other event-specific data
 *   }
 */

import { callExtension, getExtensionFunction } from '../system/extensionLoader.js';
import { onReset, ownerSignal } from "../system/teardown.js";
import { transport } from "../system/transportState.js";

// Registry of event listeners for cleanup
const eventListeners = new Map();

/** Listen under `key`, replacing whatever an earlier run of the cue left there. */
function listen(key, target, type, handler) {
  const old = eventListeners.get(key);
  if (old) (old.element || window).removeEventListener(old.type, old.handler);
  target.addEventListener(type, handler);
  eventListeners.set(key, { element: target === window ? null : target, type, handler });
}

/**
 * Handle ext() cue
 * @param {Object} ast - Parsed AST node
 * @param {Element} element - The SVG element with the cue
 */
export function handleExtCue(ast, element) {
  const args = ast.args || [];

  // Extract known params; collect everything else as extra params forwarded to
  // the extension function via ctx. This lets the DSL drive extension behaviour:
  //   ext(fn:p5sketch.setup, location:fixed, on:load)
  //   ext(fn:myLib.init, color:#f00, size:300, on:click)
  let fnPath = null;
  let onEvent = 'load';
  const extraParams = {};

  for (const arg of args) {
    if (arg.type === 'fn' || arg.type === 'function') {
      fnPath = arg.value;
    } else if (arg.type === 'on' || arg.type === 'trigger') {
      onEvent = arg.value;
    } else if (typeof arg === 'string' && arg.includes('.')) {
      fnPath = arg;
    } else if (arg.type && arg.value !== undefined) {
      // Any other named param — forward to ctx so the extension can read it
      extraParams[arg.type] = arg.value;
    }
  }

  if (!fnPath && args[0] && typeof args[0].value === 'string') {
    fnPath = args[0].value;
  }

  if (!fnPath) {
    console.warn('[ext] No function specified in cue:', ast);
    return;
  }

  // Build context: standard fields + any extra DSL params forwarded through
  const buildContext = (eventData = {}) => ({
    event: onEvent,
    element,
    fnPath,
    paramBus: window.oscillaParamBus,
    ...extraParams,   // e.g. location, color, size, etc.
    ...eventData
  });

  // Handle different event types
  switch (onEvent) {
    case 'load': {
      // Extensions load asynchronously (CDN imports may be slow). Retry
      // until the function is available rather than using a fixed timeout.
      // The wait is the run's: run again, or the project left, it stops (it
      // would else call the next project's function of the same name).
      const signal = ownerSignal(element || "ext", `ext:load:${fnPath}`);
      const _fireLoad = (attempt = 0) => {
        if (signal.aborted) return;
        const fn = getExtensionFunction(fnPath);
        if (fn) {
          callExtension(fnPath, buildContext());
        } else if (attempt < 20) {
          // Retry up to 20 times (≈ 4s total) at increasing intervals
          setTimeout(() => _fireLoad(attempt + 1), 100 + attempt * 50);
        } else {
          console.warn(`[ext] on:load gave up waiting for ${fnPath}`);
        }
      };
      setTimeout(() => _fireLoad(), 150);
      break;
    }

    case 'select':
      // Listen for polygon selection changes
      registerSelectListener(fnPath, element, buildContext);
      break;

    case 'click':
      // Listen for clicks on the element
      registerClickListener(fnPath, element, buildContext);
      break;

    case 'play':
      // Listen for playback start
      registerPlayListener(fnPath, element, buildContext);
      break;

    case 'stop':
      // Listen for playback stop
      registerStopListener(fnPath, element, buildContext);
      break;

    default:
      // Custom event - listen on window
      registerCustomListener(onEvent, fnPath, element, buildContext);
      break;
  }
}

/**
 * Register listener for polygon selection events
 */
function registerSelectListener(fnPath, element, buildContext) {
  const handler = (e) => {
    // Use the array from event detail (multi-select support)
    const selected = e.detail?.selected || [];
    const selectedSet = e.detail?.selectedSet || window._activeO2PGroups;
    const allSelectors = window._o2pPathSelectors || {};
    const all = Object.keys(allSelectors).map(uid => {
      const sel = allSelectors[uid];
      return {
        uid,
        groupId: sel.groupId,
        element: document.getElementById(sel.groupId)
      };
    });

    callExtension(fnPath, buildContext({
      selected,
      selectedSet,
      all,
      selectors: allSelectors,
      groupId: e.detail?.groupId,
      wasDeactivation: e.detail?.wasDeactivation,
      wasActivation: e.detail?.wasActivation
    }));
  };

  // Listen for selection changes
  listen(`${fnPath}:${element.id || 'anon'}`, window, 'oscilla:group-selected', handler);
}

/**
 * Register click listener on element
 */
function registerClickListener(fnPath, element, buildContext) {
  // Tag so transport-touch pointer-capture excludes this element
  // (same mechanism as audio/synth clickable cues).
  if (element.dataset) element.dataset.cueClickable = "1";
  if (element.style) element.style.cursor = "pointer";

  const handler = (e) => {
    callExtension(fnPath, buildContext({
      clickEvent: e,
      target: e.target
    }));
  };

  listen(`${fnPath}:${element.id || 'anon'}:click`, element, 'click', handler);
}

/**
 * Register play event listener
 */
function registerPlayListener(fnPath, element, buildContext) {
  const handler = () => {
    callExtension(fnPath, buildContext({
      playing: true,
      playheadX: transport.playheadX
    }));
  };

  listen(`${fnPath}:${element.id || 'anon'}:play`, window, 'oscilla:play', handler);
}

/**
 * Register stop event listener
 */
function registerStopListener(fnPath, element, buildContext) {
  const handler = () => {
    callExtension(fnPath, buildContext({
      playing: false,
      playheadX: transport.playheadX
    }));
  };

  listen(`${fnPath}:${element.id || 'anon'}:stop`, window, 'oscilla:stop', handler);
}

/**
 * Register custom event listener
 */
function registerCustomListener(eventName, fnPath, element, buildContext) {
  const handler = (e) => {
    callExtension(fnPath, buildContext({
      customEvent: e,
      detail: e.detail
    }));
  };

  listen(`${fnPath}:${element.id || 'anon'}:${eventName}`, window, eventName, handler);
}

/**
 * Cleanup all registered listeners (call on project unload)
 */
export function cleanupExtListeners() {
  for (const [, listener] of eventListeners) {
    if (listener.element) {
      listener.element.removeEventListener(listener.type, listener.handler);
    } else {
      window.removeEventListener(listener.type, listener.handler);
    }
  }
  eventListeners.clear();
}
onReset("project", cleanupExtListeners);
